/**
 * Roles and Privileges Configuration
 * Defines what each role can do and which screens they can access
 */

export const ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  REP: 'rep',
  SUPERVISOR: 'supervisor',
};

export const PRIVILEGES = {
  // Overview
  VIEW_OVERVIEW: 'view_overview',
  VIEW_DASHBOARD_STATS: 'view_dashboard_stats',

  // User Management
  VIEW_USERS: 'view_users',
  CREATE_USER: 'create_user',
  EDIT_USER: 'edit_user',
  DELETE_USER: 'delete_user',
  MANAGE_ROLES: 'manage_roles',

  // Rep Management
  VIEW_REPS: 'view_reps',
  EDIT_REP: 'edit_rep',
  DELETE_REP: 'delete_rep',
  VIEW_DEVICES: 'view_devices',

  // Targets
  VIEW_TARGETS: 'view_targets',
  CREATE_TARGETS: 'create_targets',
  EDIT_TARGETS: 'edit_targets',
  APPROVE_TARGETS: 'approve_targets',

  // Sales & Progress
  VIEW_SALES: 'view_sales',
  VIEW_PROGRESS: 'view_progress',
  RECORD_SALES: 'record_sales',
  EDIT_SALES: 'edit_sales',
  VIEW_SALES_HISTORY: 'view_sales_history',

  // Customers
  VIEW_CUSTOMERS: 'view_customers',
  CREATE_CUSTOMER: 'create_customer',
  EDIT_CUSTOMER: 'edit_customer',
  DELETE_CUSTOMER: 'delete_customer',

  // Orders
  VIEW_ORDERS: 'view_orders',
  CREATE_ORDER: 'create_order',
  EDIT_ORDER: 'edit_order',
  DELETE_ORDER: 'delete_order',

  // Product catalog
  VIEW_PRODUCTS: 'view_products',
  MANAGE_PRODUCTS: 'manage_products',

  // Product incentives (per-product quantity-based bonus)
  VIEW_INCENTIVES: 'view_incentives',
  MANAGE_INCENTIVES: 'manage_incentives',

  // Announcements & notifications
  VIEW_NOTIFICATIONS: 'view_notifications',
  MANAGE_NOTIFICATIONS: 'manage_notifications',

  // Fraud & Alerts
  VIEW_ALERTS: 'view_alerts',
  MANAGE_ALERTS: 'manage_alerts',

  // Field monitoring
  VIEW_VISIT_HISTORY: 'view_visit_history',
  VIEW_LOGS: 'view_logs', // legacy alias; maps to visit history screen
  VIEW_ATTENDANCE: 'view_attendance',
  VIEW_LEAVE: 'view_leave',
  MANAGE_LEAVE: 'manage_leave',
  VIEW_FUEL: 'view_fuel',
  VIEW_ANALYTICS: 'view_analytics',
  EXPORT_DATA: 'export_data',

  // Finances
  VIEW_SALARY_CONFIG: 'view_salary_config',
  VIEW_SALARY_PROCESSING: 'view_salary_processing',
  VIEW_SALARY_SLIPS: 'view_salary_slips',
  VIEW_PAYMENTS: 'view_payments',

  // Settings & system
  VIEW_SETTINGS: 'view_settings',
  EDIT_SETTINGS: 'edit_settings',
  VIEW_SYSTEM_LOGS: 'view_system_logs',
  MANAGE_SYSTEM: 'manage_system',
};

/**
 * Privilege editor groupings — every PRIVILEGES value must appear exactly once.
 */
export const PRIVILEGE_CATEGORIES = {
  'Overview Dashboard': [
    PRIVILEGES.VIEW_OVERVIEW,
    PRIVILEGES.VIEW_DASHBOARD_STATS,
  ],
  'User Management': [
    PRIVILEGES.VIEW_USERS,
    PRIVILEGES.CREATE_USER,
    PRIVILEGES.EDIT_USER,
    PRIVILEGES.DELETE_USER,
    PRIVILEGES.MANAGE_ROLES,
  ],
  'Representatives': [
    PRIVILEGES.VIEW_REPS,
    PRIVILEGES.EDIT_REP,
    PRIVILEGES.DELETE_REP,
    PRIVILEGES.VIEW_DEVICES,
  ],
  'Targets': [
    PRIVILEGES.VIEW_TARGETS,
    PRIVILEGES.CREATE_TARGETS,
    PRIVILEGES.EDIT_TARGETS,
    PRIVILEGES.APPROVE_TARGETS,
  ],
  'Sales Recording': [
    PRIVILEGES.VIEW_SALES,
    PRIVILEGES.VIEW_PROGRESS,
    PRIVILEGES.RECORD_SALES,
    PRIVILEGES.EDIT_SALES,
    PRIVILEGES.VIEW_SALES_HISTORY,
  ],
  'Customers': [
    PRIVILEGES.VIEW_CUSTOMERS,
    PRIVILEGES.CREATE_CUSTOMER,
    PRIVILEGES.EDIT_CUSTOMER,
    PRIVILEGES.DELETE_CUSTOMER,
  ],
  'Orders': [
    PRIVILEGES.VIEW_ORDERS,
    PRIVILEGES.CREATE_ORDER,
    PRIVILEGES.EDIT_ORDER,
    PRIVILEGES.DELETE_ORDER,
  ],
  'Product Catalog': [
    PRIVILEGES.VIEW_PRODUCTS,
    PRIVILEGES.MANAGE_PRODUCTS,
  ],
  'Product Incentives': [
    PRIVILEGES.VIEW_INCENTIVES,
    PRIVILEGES.MANAGE_INCENTIVES,
  ],
  'Announcements': [
    PRIVILEGES.VIEW_NOTIFICATIONS,
    PRIVILEGES.MANAGE_NOTIFICATIONS,
  ],
  'Fraud Alerts': [
    PRIVILEGES.VIEW_ALERTS,
    PRIVILEGES.MANAGE_ALERTS,
  ],
  'Attendance': [PRIVILEGES.VIEW_ATTENDANCE],
  'Leave Requests': [PRIVILEGES.VIEW_LEAVE, PRIVILEGES.MANAGE_LEAVE],
  'Visit History': [PRIVILEGES.VIEW_VISIT_HISTORY],
  'Fuel Expenses': [PRIVILEGES.VIEW_FUEL],
  'Analytics': [PRIVILEGES.VIEW_ANALYTICS, PRIVILEGES.EXPORT_DATA],
  'Finances — Salary Config': [PRIVILEGES.VIEW_SALARY_CONFIG],
  'Finances — Payroll': [PRIVILEGES.VIEW_SALARY_PROCESSING],
  'Finances — Salary Slips': [PRIVILEGES.VIEW_SALARY_SLIPS],
  'Finances — Payments': [PRIVILEGES.VIEW_PAYMENTS],
  'System Settings': [
    PRIVILEGES.VIEW_SETTINGS,
    PRIVILEGES.EDIT_SETTINGS,
  ],
  'System Logs': [PRIVILEGES.VIEW_SYSTEM_LOGS, PRIVILEGES.MANAGE_SYSTEM],
};

/**
 * Screen-to-route mapping with required privileges (nav + route guards).
 */
export const SCREEN_PRIVILEGES = {
  overview: {
    label: 'Overview Dashboard',
    path: '/overview',
    required: [PRIVILEGES.VIEW_OVERVIEW],
  },
  users: {
    label: 'User Management',
    path: '/users',
    required: [PRIVILEGES.VIEW_USERS],
  },
  roles: {
    label: 'Role Management',
    path: '/roles',
    required: [PRIVILEGES.MANAGE_ROLES],
  },
  settings: {
    label: 'System Settings',
    path: '/settings',
    required: [PRIVILEGES.VIEW_SETTINGS],
  },
  reps: {
    label: 'Representatives',
    path: '/reps',
    required: [PRIVILEGES.VIEW_REPS],
  },
  devices: {
    label: 'Devices',
    path: '/device-activation',
    required: [PRIVILEGES.VIEW_DEVICES],
  },
  targets: {
    label: 'Targets',
    path: '/targets',
    required: [PRIVILEGES.VIEW_TARGETS],
  },
  sales: {
    label: 'Sales Recording',
    path: '/sales',
    required: [PRIVILEGES.VIEW_SALES],
  },
  customers: {
    label: 'Customers',
    path: '/customers',
    required: [PRIVILEGES.VIEW_CUSTOMERS],
  },
  orders: {
    label: 'Orders',
    path: '/orders',
    required: [PRIVILEGES.VIEW_ORDERS],
  },
  products: {
    label: 'Product Catalog',
    path: '/products',
    required: [PRIVILEGES.VIEW_PRODUCTS],
  },
  incentives: {
    label: 'Incentive Config',
    path: '/incentives',
    required: [PRIVILEGES.VIEW_INCENTIVES],
  },
  announcements: {
    label: 'Announcements',
    path: '/announcements',
    required: [PRIVILEGES.VIEW_NOTIFICATIONS],
  },
  salaryConfig: {
    label: 'Salary Config',
    path: '/salary-config',
    required: [PRIVILEGES.VIEW_SALARY_CONFIG],
  },
  salaryProcessing: {
    label: 'Payroll',
    path: '/salary-processing',
    required: [PRIVILEGES.VIEW_SALARY_PROCESSING],
  },
  salarySlips: {
    label: 'Salary Slips',
    path: '/salary-slips',
    required: [PRIVILEGES.VIEW_SALARY_SLIPS],
  },
  payments: {
    label: 'Payments',
    path: '/payment-tracking',
    required: [PRIVILEGES.VIEW_PAYMENTS],
  },
  attendance: {
    label: 'Attendance',
    path: '/attendance',
    required: [PRIVILEGES.VIEW_ATTENDANCE],
  },
  leave: {
    label: 'Leave Requests',
    path: '/leave',
    required: [PRIVILEGES.VIEW_LEAVE],
  },
  logs: {
    label: 'Visit History',
    path: '/logs',
    required: [PRIVILEGES.VIEW_VISIT_HISTORY],
  },
  fuel: {
    label: 'Fuel Expenses',
    path: '/fuel',
    required: [PRIVILEGES.VIEW_FUEL],
  },
  analytics: {
    label: 'Analytics',
    path: '/analytics',
    required: [PRIVILEGES.VIEW_ANALYTICS],
  },
  alerts: {
    label: 'Fraud Alerts',
    path: '/alerts',
    required: [PRIVILEGES.VIEW_ALERTS],
  },
  systemLogs: {
    label: 'System Logs',
    path: '/system-logs',
    required: [PRIVILEGES.VIEW_SYSTEM_LOGS],
  },
};

/** Human-readable labels in the privilege editor */
export const PRIVILEGE_LABELS = {
  [PRIVILEGES.VIEW_VISIT_HISTORY]: 'Visit History screen',
  [PRIVILEGES.VIEW_LOGS]: 'Visit History (legacy grant)',
  [PRIVILEGES.VIEW_SYSTEM_LOGS]: 'System Logs screen',
  [PRIVILEGES.VIEW_LEAVE]: 'Leave Requests screen',
  [PRIVILEGES.MANAGE_LEAVE]: 'Approve / reject leave',
  [PRIVILEGES.VIEW_FUEL]: 'Fuel Expenses screen',
  [PRIVILEGES.VIEW_SALES]: 'Sales Recording screen',
  [PRIVILEGES.VIEW_DEVICES]: 'Devices screen',
  [PRIVILEGES.VIEW_SALARY_CONFIG]: 'Salary Config screen',
  [PRIVILEGES.VIEW_SALARY_PROCESSING]: 'Payroll screen',
  [PRIVILEGES.VIEW_SALARY_SLIPS]: 'Salary Slips screen',
  [PRIVILEGES.VIEW_PAYMENTS]: 'Payments screen',
  [PRIVILEGES.VIEW_INCENTIVES]: 'Incentive Config screen',
  [PRIVILEGES.MANAGE_INCENTIVES]: 'Edit product incentive rules',
};

/** Route guard helper */
export const screenRequiredPrivileges = (screenKey) =>
  SCREEN_PRIVILEGES[screenKey]?.required || [];

/**
 * Role-based Access Control Matrix
 */
export const ROLE_PRIVILEGES = {
  [ROLES.ADMIN]: [...Object.values(PRIVILEGES)],

  [ROLES.MANAGER]: [
    PRIVILEGES.VIEW_OVERVIEW,
    PRIVILEGES.VIEW_DASHBOARD_STATS,
    PRIVILEGES.VIEW_USERS,
    PRIVILEGES.EDIT_USER,
    PRIVILEGES.VIEW_REPS,
    PRIVILEGES.EDIT_REP,
    PRIVILEGES.VIEW_DEVICES,
    PRIVILEGES.VIEW_TARGETS,
    PRIVILEGES.EDIT_TARGETS,
    PRIVILEGES.APPROVE_TARGETS,
    PRIVILEGES.VIEW_SALES,
    PRIVILEGES.VIEW_PROGRESS,
    PRIVILEGES.VIEW_SALES_HISTORY,
    PRIVILEGES.VIEW_CUSTOMERS,
    PRIVILEGES.EDIT_CUSTOMER,
    PRIVILEGES.VIEW_ORDERS,
    PRIVILEGES.EDIT_ORDER,
    PRIVILEGES.VIEW_PRODUCTS,
    PRIVILEGES.MANAGE_PRODUCTS,
    PRIVILEGES.VIEW_INCENTIVES,
    PRIVILEGES.MANAGE_INCENTIVES,
    PRIVILEGES.VIEW_NOTIFICATIONS,
    PRIVILEGES.MANAGE_NOTIFICATIONS,
    PRIVILEGES.VIEW_SALARY_CONFIG,
    PRIVILEGES.VIEW_SALARY_PROCESSING,
    PRIVILEGES.VIEW_SALARY_SLIPS,
    PRIVILEGES.VIEW_PAYMENTS,
    PRIVILEGES.VIEW_ALERTS,
    PRIVILEGES.VIEW_VISIT_HISTORY,
    PRIVILEGES.VIEW_ATTENDANCE,
    PRIVILEGES.VIEW_LEAVE,
    PRIVILEGES.MANAGE_LEAVE,
    PRIVILEGES.VIEW_FUEL,
    PRIVILEGES.VIEW_ANALYTICS,
  ],

  [ROLES.SUPERVISOR]: [
    PRIVILEGES.VIEW_OVERVIEW,
    PRIVILEGES.VIEW_DASHBOARD_STATS,
    PRIVILEGES.VIEW_REPS,
    PRIVILEGES.VIEW_DEVICES,
    PRIVILEGES.VIEW_TARGETS,
    PRIVILEGES.VIEW_SALES,
    PRIVILEGES.VIEW_PROGRESS,
    PRIVILEGES.RECORD_SALES,
    PRIVILEGES.VIEW_SALES_HISTORY,
    PRIVILEGES.VIEW_CUSTOMERS,
    PRIVILEGES.CREATE_CUSTOMER,
    PRIVILEGES.EDIT_CUSTOMER,
    PRIVILEGES.VIEW_ORDERS,
    PRIVILEGES.VIEW_ALERTS,
    PRIVILEGES.VIEW_VISIT_HISTORY,
    PRIVILEGES.VIEW_ATTENDANCE,
    PRIVILEGES.VIEW_LEAVE,
    PRIVILEGES.MANAGE_LEAVE,
    PRIVILEGES.VIEW_FUEL,
    PRIVILEGES.VIEW_ANALYTICS,
    PRIVILEGES.VIEW_NOTIFICATIONS,
    PRIVILEGES.MANAGE_NOTIFICATIONS,
    PRIVILEGES.VIEW_INCENTIVES,
  ],

  [ROLES.REP]: [
    PRIVILEGES.VIEW_OVERVIEW,
    PRIVILEGES.VIEW_SALES,
    PRIVILEGES.VIEW_PROGRESS,
    PRIVILEGES.RECORD_SALES,
    PRIVILEGES.VIEW_SALES_HISTORY,
    PRIVILEGES.VIEW_CUSTOMERS,
    PRIVILEGES.VIEW_ORDERS,
  ],
};

/**
 * Map legacy privileges to newer screen-specific ones (saved custom grants keep working).
 */
export function expandEffectivePrivileges(privileges = []) {
  const set = new Set(Array.isArray(privileges) ? privileges : []);

  if (set.has(PRIVILEGES.VIEW_REPS)) {
    set.add(PRIVILEGES.VIEW_DEVICES);
    set.add(PRIVILEGES.VIEW_SALARY_CONFIG);
    set.add(PRIVILEGES.VIEW_SALARY_PROCESSING);
    set.add(PRIVILEGES.VIEW_SALARY_SLIPS);
    set.add(PRIVILEGES.VIEW_PAYMENTS);
  }
  if (set.has(PRIVILEGES.VIEW_PROGRESS) || set.has(PRIVILEGES.RECORD_SALES)) {
    set.add(PRIVILEGES.VIEW_SALES);
  }
  if (set.has(PRIVILEGES.VIEW_ATTENDANCE)) {
    set.add(PRIVILEGES.VIEW_LEAVE);
    set.add(PRIVILEGES.MANAGE_LEAVE);
  }
  if (set.has(PRIVILEGES.VIEW_LOGS)) {
    set.add(PRIVILEGES.VIEW_VISIT_HISTORY);
  }
  if (set.has(PRIVILEGES.VIEW_VISIT_HISTORY)) {
    set.add(PRIVILEGES.VIEW_LOGS);
  }
  // Users with other field-monitoring screens should also see Visit History
  if (
    set.has(PRIVILEGES.VIEW_ATTENDANCE) ||
    set.has(PRIVILEGES.VIEW_LEAVE) ||
    set.has(PRIVILEGES.VIEW_FUEL)
  ) {
    set.add(PRIVILEGES.VIEW_VISIT_HISTORY);
  }
  if (set.has(PRIVILEGES.VIEW_LOGS) || set.has(PRIVILEGES.VIEW_ATTENDANCE)) {
    set.add(PRIVILEGES.VIEW_FUEL);
  }
  if (set.has(PRIVILEGES.MANAGE_SYSTEM)) {
    set.add(PRIVILEGES.VIEW_SYSTEM_LOGS);
  }
  if (set.has(PRIVILEGES.MANAGE_INCENTIVES)) {
    set.add(PRIVILEGES.VIEW_INCENTIVES);
  }
  if (set.has(PRIVILEGES.MANAGE_PRODUCTS)) {
    set.add(PRIVILEGES.VIEW_PRODUCTS);
  }
  if (set.has(PRIVILEGES.EDIT_TARGETS) || set.has(PRIVILEGES.CREATE_TARGETS)) {
    set.add(PRIVILEGES.VIEW_INCENTIVES);
  }

  return [...set];
}
